// A session's logs and spans as the session view's tabs show them (APS-196, design "4 Session"):
// the logs in session order with their severity as a word, and the spans as one waterfall per
// trace. Everything here is the app's own text: shown as text, never trusted.
import type { SessionTimeline } from '../api/types'
import { httpName, parseTime } from './session'

type Event = SessionTimeline['events'][number]
type LogEvent = Extract<Event, { type: 'log' }>
type SpanEvent = Extract<Event, { type: 'span' }>

/** The severity as a word (docs/brand.md), from the OTLP severity number's range. */
export type Severity = 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace'

export function severityWord(n: number): Severity {
  if (n >= 21) return 'fatal'
  if (n >= 17) return 'error'
  if (n >= 13) return 'warn'
  if (n >= 9) return 'info'
  if (n >= 5) return 'debug'
  return 'trace'
}

export interface SessionLog {
  id: string
  /** Milliseconds from the session's start. */
  at: number
  severity: Severity
  name: string
  body: string
  attributes: [string, string][]
  /** The log's trace, empty when it has none (APS-315). */
  traceId: string
}

export function sessionLogs(timeline: SessionTimeline, startedAt: number): SessionLog[] {
  return timeline.events
    .filter((e): e is LogEvent => e.type === 'log')
    .map((e) => ({
      id: e.id,
      at: parseTime(e.ts) - startedAt,
      severity: severityWord(e.severity_number),
      name: e.event_name,
      body: e.body,
      attributes: Object.entries(e.attributes).sort(([a], [b]) => a.localeCompare(b)),
      traceId: e.trace_id ?? '',
    }))
    .sort((a, b) => a.at - b.at)
}

export interface WaterfallSpan {
  id: string
  name: string
  /** How deep under the trace's root, for the indent. */
  depth: number
  /** Milliseconds from the trace's first span. */
  offset: number
  duration: number
  failed: boolean
  statusMessage: string
}

export interface Trace {
  traceId: string
  /** Milliseconds from the session's start to the trace's first span. */
  at: number
  /** From its first span's start to its last span's end. */
  length: number
  spans: WaterfallSpan[]
}

/**
 * The session's spans, one waterfall per trace, traces in time order. In a trace, each span
 * follows its parent (a parent not in the session puts the span at the top) and siblings go by
 * start time.
 */
export function sessionTraces(timeline: SessionTimeline, startedAt: number): Trace[] {
  const byTrace = new Map<string, SpanEvent[]>()
  for (const e of timeline.events)
    if (e.type === 'span') byTrace.set(e.trace_id, [...(byTrace.get(e.trace_id) ?? []), e])
  const traces: Trace[] = []
  for (const [traceId, spans] of byTrace) {
    const start = Math.min(...spans.map((s) => parseTime(s.ts)))
    const end = Math.max(...spans.map((s) => parseTime(s.end_ts)))
    const ids = new Set(spans.map((s) => s.span_id))
    const children = new Map<string, SpanEvent[]>()
    for (const s of spans) {
      const parent = s.parent_span_id && ids.has(s.parent_span_id) ? s.parent_span_id : ''
      children.set(parent, [...(children.get(parent) ?? []), s])
    }
    const ordered: WaterfallSpan[] = []
    const seen = new Set<string>()
    const byStart = (list: SpanEvent[]) =>
      [...list].sort((a, b) => parseTime(a.ts) - parseTime(b.ts))
    const visit = (s: SpanEvent, depth: number) => {
      // Each span once, even in a parent loop the app sent.
      if (seen.has(s.span_id)) return
      seen.add(s.span_id)
      ordered.push({
        id: s.id,
        // A request by its method and route, as in the events table (APS-393).
        name: httpName(s.attributes, s.name) ?? s.name,
        depth,
        offset: parseTime(s.ts) - start,
        duration: s.duration_ns / 1e6,
        failed: s.status_code === 'ERROR',
        statusMessage: s.status_message,
      })
      for (const c of byStart(children.get(s.span_id) ?? [])) visit(c, depth + 1)
    }
    for (const s of byStart(children.get('') ?? [])) visit(s, 0)
    // Spans whose parents only point at each other: at the top, by time.
    for (const s of byStart(spans)) visit(s, 0)
    traces.push({
      traceId,
      at: start - startedAt,
      length: Math.max(0, end - start),
      spans: ordered,
    })
  }
  return traces.sort((a, b) => a.at - b.at)
}
